import { Link } from "react-router-dom";
import { LogoFull } from "./Logo";
import { useEffect } from "react";

export default function Terms() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-50">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="logo-link"><LogoFull size={30} /></Link>
          <nav className="flex items-center gap-3">
            <Link to="/login" className="px-4 py-2 font-semibold text-[#0A0A0A] hover:text-[#1A3DFF] transition-colors text-[15px]">Sign in</Link>
            <Link to="/register" className="btn-primary">Get started free</Link>
          </nav>
        </div>
      </header>

      <div className="max-w-[720px] mx-auto px-6 py-16">
        <h1 className="font-display text-[36px] tracking-[-0.03em] text-[#0A0A0A] mb-2" data-testid="terms-title">Terms of Service</h1>
        <p className="text-[13px] text-[#9CA3AF] mb-12">Last updated: April 7, 2026</p>

        <div className="prose-verobill space-y-8 text-[15px] leading-[1.75] text-[#374151]">
          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">1. Acceptance of terms</h2>
            <p>By accessing or using VeroBill ("the Service"), operated by Brightridge Partners Ltd, you agree to be bound by these Terms of Service. If you do not agree, do not use the Service. These terms apply to all users, including free and paid subscribers.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">2. Description of service</h2>
            <p>VeroBill is an online invoicing platform that allows you to create, send, and manage invoices, estimates, and expenses. The Service includes PDF generation, multi-currency support, tax compliance features, and integrations with third-party payment processors.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">3. Accounts</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>You must provide accurate and complete registration information.</li>
              <li>You are responsible for maintaining the security of your account credentials.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
              <li>You must be at least 16 years old to use the Service.</li>
              <li>One person or entity may not maintain more than one free account.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">4. Free and paid plans</h2>
            <p><strong>Free plan:</strong> Includes up to 10 invoices per month, unlimited clients, unlimited estimates, unlimited expense tracking, and access to one PDF template. The free plan is subject to fair use — we reserve the right to limit accounts that abuse the free tier.</p>
            <p><strong>Pro plan:</strong> Available at $9/month or $79/year. Includes unlimited invoices, recurring invoices, all PDF templates, custom email domain, and removal of VeroBill footer branding.</p>
            <p><strong>Lifetime deal:</strong> A one-time payment of $99 grants lifetime access to all Pro features, including all future updates. Lifetime deals are non-transferable.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">5. Payments and billing</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Paid subscriptions are billed in advance on a monthly or annual basis via Stripe.</li>
              <li>All fees are non-refundable except as required by law or at our sole discretion.</li>
              <li>We may change pricing with 30 days' notice. Existing subscriptions will not be affected until renewal.</li>
              <li>Lifetime deals are a one-time payment and are not subject to future price changes.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">6. Your data and content</h2>
            <p>You retain full ownership of all data you create through the Service, including invoices, client information, estimates, and expenses. We do not claim any intellectual property rights over your content.</p>
            <p>You grant us a limited licence to store, process, and display your data solely for the purpose of providing the Service to you.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">7. Payment processing</h2>
            <p>VeroBill allows you to connect your own payment provider accounts (Stripe, PayPal, Razorpay, Wise) to receive payments from your clients. You are responsible for:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Complying with the terms of your payment provider</li>
              <li>Any fees charged by your payment provider</li>
              <li>Tax obligations related to payments you receive</li>
            </ul>
            <p className="mt-3">VeroBill does not take a commission on payments you receive from your clients. 100% of the payment goes to your connected account.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">8. Acceptable use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Use the Service for any unlawful purpose or to send fraudulent invoices</li>
              <li>Attempt to gain unauthorised access to other accounts or our systems</li>
              <li>Reverse-engineer, decompile, or attempt to extract the source code of the Service</li>
              <li>Use the Service to send spam, phishing emails, or unsolicited communications</li>
              <li>Exceed reasonable usage limits on the free plan (e.g., creating multiple accounts)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">9. Disclaimer of warranties</h2>
            <p>The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted, secure, or error-free.</p>
            <p>VeroBill is not an accounting firm or tax advisor. While we provide tax templates for various countries, you are solely responsible for the accuracy of your invoices and tax compliance.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">10. Limitation of liability</h2>
            <p>To the maximum extent permitted by law, VeroBill shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities, arising from your use of the Service.</p>
            <p>Our total liability for any claim arising from or related to the Service shall not exceed the amount you paid us in the 12 months prior to the claim.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">11. Termination</h2>
            <p>You may cancel your account at any time. We may suspend or terminate your account if you violate these terms or engage in abusive behaviour.</p>
            <p>Upon termination, your data will be retained for 30 days to allow you to export it, after which it will be permanently deleted.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">12. Changes to terms</h2>
            <p>We may update these Terms from time to time. Material changes will be communicated via email or through a notice on the Service at least 30 days before they take effect. Continued use after the effective date constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">13. Governing law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to conflict of law principles.</p>
          </section>

          <section>
            <h2 className="text-[20px] font-bold text-[#0A0A0A] mb-3">14. Contact</h2>
            <p>If you have questions about these Terms, contact us at:</p>
            <p className="mt-2"><strong>Email:</strong> hello@verobill.com</p>
            <p className="mt-1"><strong>Company:</strong> Brightridge Partners Ltd</p>
            <p className="mt-1">71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom</p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] py-8 px-6 bg-white">
        <div className="max-w-[720px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <LogoFull size={22} />
          <div className="flex items-center gap-4 text-[12px] text-[#9CA3AF]">
            <Link to="/terms" className="hover:text-[#0A0A0A]">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-[#0A0A0A]">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
